'use client';

import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { NumberInput, type NumberInputProps, type NumberInputText } from '@magicschool/spellbook/components/NumberInput';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Text, type TextProps } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

export type EditableNumberInputHandle = {
  /** Reference to the display text element shown when not in edit mode. */
  text: HTMLElement | null;
  /** Reference to the underlying number input element used during editing. */
  input: HTMLInputElement | null;
};

export type EditableNumberInputProps = Omit<
  NumberInputProps,
  'value' | 'defaultValue' | 'onChange' | 'size' | 'readOnly' | 'text' | 'disabled'
> & {
  /** The current numeric value. When provided, the component operates in controlled mode. */
  value?: number;
  /** The default value for uncontrolled usage. */
  defaultValue?: number;
  /** Whether the component should take the full width of its container. */
  fullWidth?: boolean;
  /** Callback fired when the value is saved (on blur or Enter). */
  onSave?: (value: number | null) => void;
  /** Whether the component is in edit mode. When provided, controls editing state externally. */
  isEditing?: boolean;
  /** Callback fired when the editing state changes. */
  onIsEditingChange?: (isEditing: boolean) => void;
  /** The text color used for the display value. Defaults to 'default'. */
  color?: TextProps['color'];
  /** Additional class name applied to the display text element. */
  textClassName?: string;
  /** Additional class name applied to the input element during editing. */
  inputClassName?: string;
  /** Configuration for accessible text labels. */
  text?: NumberInputText & {
    /** Label for the edit button. Defaults to 'Edit'. */
    editButtonLabel?: string;
    /** Placeholder shown when there is no value. Defaults to 'No value'. */
    textPlaceholder?: string;
    /** Accessible label for the input field. Defaults to 'Edit number'. */
    inputLabel?: string;
  };
  /**
   * The id of an external label element. Forwarded to `NumberInput` so stepper buttons use
   * `aria-labelledby` combining their own action label with the field label, e.g. "Increment, Points".
   */
  labelId?: string;
};

// ─── Slots ───────────────────────────────────────────────────────────────────

/**
 * Optional unit label shown between the value and the edit button in display mode,
 * and between the value and the stepper buttons in edit mode.
 *
 * @example
 * <EditableNumberInput defaultValue={42}>
 *   <EditableNumberInput.Unit>pts</EditableNumberInput.Unit>
 * </EditableNumberInput>
 */
export type EditableNumberInputUnitSlotProps = React.HTMLAttributes<HTMLSpanElement> & {
  /** @internal Injected by EditableNumberInput — do not pass manually. */
  variant?: TextProps['variant'];
};

export const EditableNumberInputUnitSlot = Slot<EditableNumberInputUnitSlotProps>({
  displayName: 'EditableNumberInput.Unit',
  renderDefault: ({ children, className, variant: _variant, ...props }) => (
    <span data-spellbook-name="editable-number-input-unit" className={cn('shrink-0', className)} {...props}>
      {children}
    </span>
  ),
});

// ─── Variants ────────────────────────────────────────────────────────────────

const wrapperVariants = cva(
  [
    'group/editable-number',
    'before:bg-surface-transparent-0',
    '[&:has(*:focus-visible)]:before:bg-surface-transparent-1',
    'before:absolute',
    'before:inset-0',
    'before:rounded-lg',
    'before:pointer-events-none',
    'before:transition-colors',
    'relative',
    'rounded-lg',
    'transition-colors',
    'before:border',
    'before:border-transparent',
    'hover:before:bg-surface-transparent-1',
    'p-1',
    '-translate-x-1',
  ],
  {
    variants: {
      fullWidth: {
        true: 'w-full flex',
        false: 'w-fit inline-flex max-w-full',
      },
      invalid: {
        true: ['before:border-error-stroke', 'hover:before:border-error-stroke', 'focus-within:before:border-error-stroke'],
        false: '',
      },
      isEditing: {
        true: '!hover:before:bg-surface-transparent-0 before:!bg-surface-transparent-0 before:!border-transparent before:!transition-none',
        false: '',
      },
    },
  },
);

const editIconButtonVariants = cva(
  [
    'overflow-visible',
    'h-0',
    'opacity-0',
    'transition-opacity',
    'group-hover/editable-number:opacity-100',
    'focus-visible:opacity-100',
    'focus-visible:!ring-0',
    'focus-visible:!ring-offset-0',
    'focus-visible:after:ring-2',
    'focus-visible:after:ring-inset',
    'focus-visible:after:ring-focus-ring',
    'active:bg-surface-transparent-0',
    'justify-self-end',
    'hover:bg-surface-transparent-0',
    'after:absolute',
    'after:rounded-lg',
    'after:bg-surface-transparent-0',
    'after:inset-0',
    'w-6',
  ],
  {
    variants: {
      isEditing: {
        true: 'focus-visible:opacity-0',
        false: '',
      },
    },
  },
);

const displayRowVariants = cva('flex items-center gap-1', {
  variants: {
    isEditing: {
      true: 'hidden',
      false: '',
    },
  },
});

const displayTextVariants = cva('h-auto shrink-0 min-h-control-h-xs flex items-center', {
  variants: {
    hasValue: {
      true: '',
      false: '!text-placeholder',
    },
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const EditableNumberInputBase = forwardRef<EditableNumberInputHandle, EditableNumberInputProps>(
  (
    {
      children,
      className,
      textClassName,
      inputClassName,
      invalid = false,
      fullWidth = false,
      value,
      defaultValue,
      onSave,
      isEditing: providedIsEditing,
      onIsEditingChange,
      color = 'default',
      id: providedId,
      min,
      max,
      text = {},
      labelId,
      ...props
    },
    forwardedRef,
  ) => {
    const { editButtonLabel = 'Edit', textPlaceholder = 'No value', inputLabel = 'Edit number', increment, decrement } = text;

    const id = useId(providedId);
    const textRef = useRef<HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const isControlled = value !== undefined;
    const isEditingControlled = providedIsEditing !== undefined;

    const [internalValue, setInternalValue] = useState<number | undefined>(defaultValue);
    const [draftValue, setDraftValue] = useState<string>('');
    const [internalIsEditing, setInternalIsEditing] = useState(false);

    const currentValue = isControlled ? value : internalValue;
    const isEditing = isEditingControlled ? providedIsEditing : internalIsEditing;

    useImperativeHandle(forwardedRef, () => ({
      text: textRef.current,
      input: inputRef.current,
    }));

    const { slots } = useSlots(children, {
      unit: { Component: EditableNumberInputUnitSlot },
    });

    const setIsEditing = useCallback(
      (next: boolean) => {
        if (!isEditingControlled) setInternalIsEditing(next);
        onIsEditingChange?.(next);
      },
      [isEditingControlled, onIsEditingChange],
    );

    const handleEditButtonClick = () => {
      const initialDraftValue = currentValue !== undefined ? String(currentValue) : '';
      setDraftValue(initialDraftValue);
      setIsEditing(true);
      requestAnimationFrame(() => {
        const inputEl = inputRef.current;
        if (!inputEl) return;
        inputEl.focus();
        // Only select the pre-filled text if it hasn't been edited yet — this callback
        // runs on the next paint, so a fast typist (or a test with no input delay) can
        // already have changed the value by the time it fires. Selecting unconditionally
        // would re-select and let the next keystroke silently overwrite what they typed.
        if (inputEl.value === initialDraftValue) inputEl.select();
      });
    };

    const save = useCallback(
      (options: { disableFocusBehavior?: boolean } = {}) => {
        if (!isEditing) return;
        setIsEditing(false);

        if (!options.disableFocusBehavior) {
          requestAnimationFrame(() => editButtonRef.current?.focus());
        }

        const parsed = draftValue === '' ? null : Number(draftValue);
        const clamped = parsed === null ? null : min !== undefined && parsed < min ? min : max !== undefined && parsed > max ? max : parsed;

        if (!isControlled) {
          setInternalValue(clamped ?? undefined);
        }

        onSave?.(clamped);
      },
      [setIsEditing, isEditing, draftValue, isControlled, min, max, onSave],
    );

    const handleInputWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      // Only save when focus leaves the entire editing area (not just moving between input and stepper buttons)
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      save({ disableFocusBehavior: true });
    };

    const displayText = currentValue !== undefined ? String(currentValue) : '';

    return (
      <div data-spellbook-name="editable-number-input" className={cn(wrapperVariants({ fullWidth, invalid, isEditing }), className)}>
        {/* Display row — hidden when editing, takes up no space */}
        <div className={cn(displayRowVariants({ isEditing }), fullWidth ? 'w-full justify-between' : 'w-fit')}>
          <Text
            ref={textRef}
            element="span"
            color={color}
            className={cn(displayTextVariants({ hasValue: Boolean(displayText) }), textClassName)}
          >
            {displayText || textPlaceholder}
          </Text>

          {slots.unit && (
            <Text element="span" color={color} className="shrink-0">
              {slots.unit}
            </Text>
          )}

          <IconButton
            ref={editButtonRef}
            size="sm"
            variant="ghost"
            color="neutral"
            name="pencil-line"
            className={editIconButtonVariants({ isEditing })}
            onClick={handleEditButtonClick}
            aria-label={editButtonLabel}
            tabIndex={isEditing ? -1 : 0}
            aria-controls={id}
          />
        </div>

        {/* Edit row — hidden when not editing, always in flow so height is stable */}
        <div
          className={cn(!isEditing && 'hidden', isEditing && (fullWidth ? 'w-full' : 'w-fit'))}
          inert={!isEditing}
          onBlur={handleInputWrapperBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || (e.key === 'Enter' && e.target === inputRef.current)) {
              // Escape or Enter saves; Enter only fires from the input
              // (Enter on +/- buttons should trigger the button click instead)
              save();
            }
          }}
        >
          <label htmlFor={id} className="sr-only">
            {inputLabel}
          </label>
          <NumberInput
            ref={inputRef}
            id={id}
            invalid={invalid}
            min={min}
            max={max}
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            className={cn('sm:min-w-0 !h-control-h-xs !px-1.5', inputClassName)}
            stepperSize="xs"
            text={{ increment, decrement }}
            labelId={labelId}
            {...props}
          >
            {slots.unit && <NumberInput.Unit>{slots.unit}</NumberInput.Unit>}
          </NumberInput>
        </div>
      </div>
    );
  },
);

EditableNumberInputBase.displayName = 'EditableNumberInput';

export const EditableNumberInput = Object.assign(EditableNumberInputBase, {
  Unit: EditableNumberInputUnitSlot,
});
